import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Droppable, Draggable } from "react-beautiful-dnd";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Button, IconButton, Paper } from '@mui/material';
import PopMenu from "./PopMenu";
import previewIcon from "../../Assets/Group 1753.svg";
import noteIcon from "../../Assets/wf-icon.svg";
import TablePagination from "@mui/material/TablePagination";
import { FaFile } from "react-icons/fa"; // For yellow note icon
import Tooltip from "@mui/material/Tooltip";
import CustomToaster, { notify } from "../Common Components/Custom_Toaster";

const DraggableTable = ({
  row,
  heading,
  template,
  workflow,
  task,
  droppableId,
  cloneClick,
  editClick,
  getJsonValue,
  openPopup,
  openImgPreview,
  handleNoteClick,
  onEventClick,
  selectedRowId,
  globalEvent,
  searchText,
}) => {

  // State for sorting
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [sortedRows, setSortedRows] = useState(row);
  /* ===================== PAGINATION STATE ===================== */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    globalEvent ? 200 : 10
  );

  // Get sortable fields based on template and workflow status
  const getSortableFields = () => {
    if (template) {
      if (workflow) {
        // For workflow, exclude sNo, publish, menu, preview
        return Object.keys(row[0] || {}).filter(key =>
          !['sNo', 'menu', 'preview', 'note', 'uniqueId', 'templateData', 'item', 'addFilterDetails'].includes(key)
        );
      } else {
        // For subflow, exclude sNo, publish, menu, preview
        return Object.keys(row[0] || {}).filter(key =>
          !['sNo', 'menu', 'preview', 'note', 'uniqueId', 'templateData', 'item'].includes(key)
        );
      }
    } else {
      // For events, exclude sNo and other non-displayed fields
      return Object.keys(row[0] || {}).filter(key =>
        key !== 'sNo' && key !== 'eventColor' && key !== 'uniqueId' && key !== 'templateData' && key !== 'item'
      );
    }
  };

  // Function to handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    // Sort the rows
    const sortedData = [...row].sort((a, b) => {
      // Handle string comparison
      if (typeof a[property] === 'string' && typeof b[property] === 'string') {
        if (newOrder === 'asc') {
          return a[property].localeCompare(b[property]);
        } else {
          return b[property].localeCompare(a[property]);
        }
      }
      // Handle number comparison
      else {
        if (newOrder === 'asc') {
          return a[property] - b[property];
        } else {
          return b[property] - a[property];
        }
      }
    });

    sortedData.map((item, index) => {
      item.sNo = (index + 1).toString().padStart(2, '0'); // Ensure 'sNo' remains in order, e.g., 01, 02, 03
      return item;
    });

    setSortedRows(sortedData);
  };

  /* ===================== PAGINATION HANDLERS ===================== */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // Use sortedRows if we have sorting, otherwise use the original rows
  const displayRows = orderBy ? sortedRows : row;

  const paginatedRows = displayRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
useEffect(() => {
    setPage(0);
}, [searchText]);
  const menuOptions = ['Edit', 'Delete', 'Download as pdf'];

  //Style for table cell and table row
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      fontSize: 15,
      backgroundColor: theme.palette.common.white,
      // color: theme.palette.common.black,
      color: "#757ea5",
      borderBottom: "1px solid #cfd4e9",
      padding: "8px",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 15,
      fontWeight: "600",
      padding: "0px 10px",
      fontFamily: "Montserrat, sans-serif",
      margin: "5px 0px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: "#F3FBFF",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#FFFFFF",
    },
    "&:last-child td": {
      border: 0,
    },
    height: "45px",
    alignContent: "center",
  }));

  const EmptyTableMessage = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '22px',
      width: '100%',
      height: 'auto'
    }}>
      <p>No {template ? workflow ? 'Workflows' : "Subflows" : 'Events'} found...</p>
    </div>
  );

  // Get sortable fields
  const sortableFields = getSortableFields();
  const showDragIcon = !["eventCreateTable", "subflowlistTable", "workflowlistTable"].includes(droppableId);
  return (
    <TableContainer
      component={Paper}
      sx={{
        border: "1px solid #cfd4e9",
        height: "573px",
        maxHeight: (template || task) ? "80%" : "80%",
        width: (template || task) ? "96%" : "100%",
        margin: "20px 30px",
        overflow: "auto",

        /* Custom Scrollbar Styles */
        "&::-webkit-scrollbar": {
          width: "8px", // Adjust width
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#cfd4e9", // Scroll thumb color
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f0f0f0", // Track color
        },
        "&::-webkit-scrollbar-button": {
          display: "none", // Hide up/down arrows
        },
      }}
    >
      {row.length === 0 ? (
        <EmptyTableMessage />
      ) : (
        <Table sx={{ minWidth: (template || task) ? 700 : 300 }} aria-label="customized table" stickyHeader>
          <TableHead>
            <TableRow>
              {/* Drag indicator column */}
              <StyledTableCell>
                {/* No sort for draggable column */}
              </StyledTableCell>

              {/* Main headers with sorting */}
              {heading.slice(1).map((head, index) => {
                const correspondingField = template || workflow ? sortableFields[index - 1] : sortableFields[index];
                return (
                  <StyledTableCell key={index}>
                    {sortableFields.includes(correspondingField) ? (
                      <TableSortLabel
                        active={orderBy === correspondingField}
                        direction={orderBy === correspondingField ? order : 'asc'}
                        onClick={() => handleRequestSort(correspondingField)}
                        sx={{
                          color: "#757ea5",
                          '&.MuiTableSortLabel-active': {
                            color: "#02ACF6",
                          },
                        }}
                      >
                        {head}
                      </TableSortLabel>
                    ) : (
                      head
                    )}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <Droppable droppableId={droppableId} type="event">
            {(provided, snapshot) => (
              <TableBody
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  backgroundColor: snapshot.isDraggingOver ? '#e3f2fd' : 'inherit',
                  transition: 'background-color 0.2s ease'
                }}
              >
                {paginatedRows.map((item, index) => (
                  <Draggable
                    key={item.sNo}
                    draggableId={item.sNo.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <StyledTableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.8 : 1
                        }}
                      >
                        {/* Drag Handle ONLY for eventCreateTable */}

                        <StyledTableCell
                          align="left"
                          {...provided.dragHandleProps}
                        >
                          {showDragIcon && (
                            <DragIndicatorIcon style={{ color: "#757ea5" }} />
                          )}
                        </StyledTableCell>

                        {
                          template ?
                            Object.keys(item).slice(0, template ? workflow ? -4 : -3 : -1).map((key, index) => (
                              key === 'publish' || key === 'unpublish' ?
                                <>
                                  <StyledTableCell key={key}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      sx={{
                                        textTransform: 'none',
                                        width: '110px',
                                        height: '25px',
                                        //cursor: item[key] === "Publish" ? 'pointer' : 'not-allowed',
                                        cursor: 'pointer',
                                        backgroundColor: item[key] === "Publish" ? '#02ACF6' : '#F6F7FC',
                                        color: item[key] === "Publish" ? '#FFFFFF' : '#757EA5',
                                        border: item[key] === "Publish" ? '1px solid #02ACF6' : '1px solid #757EA5',
                                        boxShadow: 'none',
                                      }}
                                      onClick={item[key] === "Publish" || item[key] === "Unpublish" ? () => openPopup(item[key] === "Publish" ? "unpublish" : "publish", item.uniqueId, template ? workflow ? "workflow" : "subflow" : "event", template ? workflow ? item.workflowName : item.subflowName : "template") : null}
                                    >
                                      {item[key]}
                                    </Button>
                                  </StyledTableCell>
                                  

                                  {workflow ? (
                                    <StyledTableCell align="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleNoteClick && handleNoteClick(item)}
                                      >
                                        <FaFile size={20} color="#f59210" /> {/* Yellow note icon */}
                                      </IconButton>
                                    </StyledTableCell>) : null}
                                </>
                                :
                                key === 'menu' ?
                                  <StyledTableCell key={key}>
                                    <PopMenu
                                      menuOption={menuOptions}
                                      // cloneClick={() => { cloneClick(item.templateData, item.uniqueId, item.item, workflow ? item.workflowName : item.subflowName) }}
                                      editClick={() => { editClick(item.templateData, item.uniqueId, item.item, workflow ? item.workflowName : item.subflowName) }}
                                      deleteClick={() => openPopup('delete', item.uniqueId, template ? workflow ? "workflow" : "subflow" : "event", template ? workflow ? item.workflowName : item.subflowName : "template")}
                                      id={item.uniqueId}
                                      workflow={workflow}
                                      isPublished={item.item.isPublished} // Pass isPublished flag here
                                    />
                                    {/* <PopMenu
                                      menuOption={menuOptions}
                                      cloneClick={() => {
                                        cloneClick(
                                          item.item.templateData,
                                          item.uniqueId,
                                          item.item,
                                          workflow ? item.workflowName : item.subflowName
                                        );
                                      }}
                                      // Provide a no-op if published
                                      editClick={
                                        item.item.isPublished
                                          ? () => {
                                                notify(
                                                  "You cannot edit a template once it has been published.",
                                                  false
                                                ); } // no-op function
                                          : () => {
                                            editClick(
                                              item.item.templateData,
                                              item.uniqueId,
                                              item.item,
                                              workflow ? item.workflowName : item.subflowName
                                            );
                                          }
                                      }
                                      deleteClick={
                                        item.item.isPublished
                                          ? () => { notify(
                                                  "You cannot delete a template once it has been published.",
                                                  false
                                                ); } // no-op function
                                          : () =>
                                            openPopup(
                                              "delete",
                                              item.uniqueId,
                                              template ? (workflow ? "workflow" : "subflow") : "event",
                                              template
                                                ? workflow
                                                  ? item.workflowName
                                                  : item.subflowName
                                                : "template"
                                            )
                                      }
                                      id={item.uniqueId}
                                      workflow={workflow}
                                    /> */}
                                  </StyledTableCell> :
                                  key === 'note' ?
                                    <>
                                      <StyledTableCell key={key}>
                                        <IconButton onClick={() => openImgPreview(item.uniqueId, workflow ? "Workflow" : "SubWorkflow")}>
                                          <img src={previewIcon} alt="Preview" style={{ width: '1.8rem', height: '1.8rem' }} />
                                        </IconButton>
                                      </StyledTableCell>

                                    </>
                                    :
                                    workflow ?
                                      <StyledTableCell
                                        key={key}
                                        onClick={key === 'workflowName' ? () => { editClick(item.templateData, item.uniqueId, item.item, item.workflowName) } : null}
                                        style={{
                                          color: key === 'workflowName' ? '#02ACF6' : '#121B45',
                                          textTransform: key === 'workflowName' ? "uppercase" : "none",
                                          cursor: key === 'workflowName' ? 'pointer' : 'auto'
                                        }}
                                      >
                                        {item[key]}
                                      </StyledTableCell> :
                                      <StyledTableCell
                                        key={key}
                                        onClick={key === 'subflowName' ? () => { editClick(item.templateData, item.uniqueId, item.item, item.subflowName) } : null}
                                        style={{
                                          color: key === 'subflowName' ? '#02ACF6' : '#121B45',
                                          cursor: key === 'subflowName' ? 'pointer' : 'auto',
                                          textTransform: key === 'subflowName' ? "uppercase" : "none"
                                        }}
                                      >
                                        {item[key]}
                                      </StyledTableCell>
                            )) :

                            Object.keys(item)
                              .slice(0, droppableId === "eventCreateTable" ? -5 :-4)
                              .map((key) =>
                                key !== "sNo" &&  key !== "permission" && key !== "iconName" && (
                                  <StyledTableCell key={key} style={{
                                    backgroundColor:
                                      item.eventId === selectedRowId ? "#b7cff7" : "transparent",
                                  }}
                                  >

                                    {key === "eventName" && !showDragIcon ? (
                                      <Tooltip title={item.eventCode} arrow>
                                        <p
                                          style={{
                                            margin: 0,
                                            cursor: "pointer",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();          // ✅ prevent row/drag conflicts
                                            onEventClick && onEventClick(item);

                                          }}
                                        >
                                          {item.eventName}
                                        </p>
                                      </Tooltip>
                                    ) : key === "eventId" ? null : (
                                    <Tooltip title={item.eventCode} arrow>  <p style={{ margin: 0 }}>{item[key]}</p> </Tooltip>)}
                                  </StyledTableCell>

                                ))
                        }

                      </StyledTableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      )}
      {/* ===================== PAGINATION ===================== */}
      <TablePagination
        component="div"
        count={displayRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
    
  );
        <CustomToaster />

};

export default DraggableTable;